import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, Info, Save } from "lucide-react";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const fmtVal = (v, fmt) => {
  const n = Number(v || 0);
  if (fmt === "int") return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
  if (fmt === "miles") return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
  return n.toLocaleString("en-US", { minimumFractionDigits: 5, maximumFractionDigits: 5 });
};

export default function CostoProveedores() {
  const { activeCompanyId, activeCompany, can } = useAuth();
  const canEdit = can("costo_proveedores", "edit");
  const now = new Date();
  const [mes, setMes] = useState(String(now.getMonth() + 1));
  const [anio, setAnio] = useState(String(now.getFullYear()));
  const [terminal, setTerminal] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState({}); // { key: { proveedor: valor } }
  const [saving, setSaving] = useState(false);
  const [paramEdits, setParamEdits] = useState({ densidad: "", litros_galon: "" });
  const [savingParams, setSavingParams] = useState(false);

  const syncEdits = (d) => {
    const e = {};
    (d?.rows || []).forEach((row) => {
      if (row.editable) {
        e[row.key] = {};
        (d.providers || []).forEach((p, i) => { e[row.key][p] = row.values[i]; });
      }
    });
    setEdits(e);
    setParamEdits({
      densidad: d?.params?.densidad ?? 0.505,
      litros_galon: d?.params?.litros_galon ?? 3.78541,
    });
  };

  const load = () => {
    setLoading(true);
    api.get(`/reportes/costo-proveedores?mes=${mes}&anio=${anio}&terminal=${encodeURIComponent(terminal)}`)
      .then((r) => { setData(r.data); syncEdits(r.data); })
      .catch(() => toast.error("No se pudo cargar el reporte"))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [mes, anio, terminal]);

  const exportXlsx = () => {
    const url = `${API}/reportes/costo-proveedores/export?mes=${mes}&anio=${anio}&terminal=${encodeURIComponent(terminal)}&cid=${activeCompanyId}&auth=${localStorage.getItem("token")}`;
    const a = document.createElement("a");
    a.href = url; a.download = "";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const saveManual = async (key) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const r = await api.post(`/reportes/costo-proveedores/manual`, {
        mes: Number(mes), anio: Number(anio), key, values: edits[key] || {},
      });
      setData(r.data); syncEdits(r.data);
      toast.success("Guardado", { duration: 1000 });
    } catch {
      toast.error("No se pudo guardar la captura");
    } finally {
      setSaving(false);
    }
  };

  const saveParams = async () => {
    if (!canEdit) return;
    setSavingParams(true);
    try {
      const r = await api.post(`/reportes/costo-proveedores/params`, {
        mes: Number(mes), anio: Number(anio),
        densidad: Number(paramEdits.densidad || 0),
        litros_galon: Number(paramEdits.litros_galon || 0),
      });
      setData(r.data); syncEdits(r.data);
      toast.success("Guardado", { duration: 1000 });
    } catch {
      toast.error("No se pudieron guardar los parámetros");
    } finally {
      setSavingParams(false);
    }
  };

  const provs = data?.providers || [];
  const hasData = provs.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="cp-mes" value={mes} onChange={(e) => setMes(e.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
            {MESES.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
          </select>
          <input data-testid="cp-anio" type="number" value={anio} onChange={(e) => setAnio(e.target.value)} className="w-28 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
          <select data-testid="cp-terminal" value={terminal} onChange={(e) => setTerminal(e.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 max-w-[260px]">
            <option value="">Todas las terminales</option>
            {(data?.terminales || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <button data-testid="cp-export" onClick={exportXlsx} disabled={!hasData}
          className="bg-emerald-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50">
          <FileSpreadsheet className="h-4 w-4" /> Descargar Excel (.xlsx)
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /> Cargando…</div>
      ) : !hasData ? (
        <div className="py-20 text-center text-slate-400" data-testid="cp-sin-datos">Sin datos de Compras para {MESES[Number(mes) - 1]} {anio}.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
          <div className="p-4 min-w-fit">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="font-heading text-xl font-bold text-slate-900 border-b-4 border-red-600 pb-2 inline-block">{data.titulo}</h2>
              <span className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="cp-autosave-indicator">
                {(saving || savingParams)
                  ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Guardando…</>)
                  : (<><Save className="h-3.5 w-3.5" /> Guardado automático</>)}
              </span>
            </div>
            <div className="flex gap-8 mt-4">
              <table className="text-sm border-separate" style={{ borderSpacing: 0 }} data-testid="cp-table">
                <thead>
                  <tr>
                    <th className="bg-white" />
                    <th colSpan={provs.length} className="bg-[#1F3A5F] text-white font-bold text-center py-2 px-4 border border-white text-base">{activeCompany?.nombre || ""}</th>
                  </tr>
                  <tr>
                    <th className="bg-white" />
                    {provs.map((p) => (
                      <th key={p} className="bg-[#1F3A5F] text-white font-bold text-center py-2 px-3 border border-white whitespace-nowrap">{p}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={row.key}>
                      <td className="bg-[#1F3A5F] text-white font-bold text-center py-2 px-3 border border-white text-xs whitespace-nowrap" data-testid={`cp-row-${row.key}`}>{row.label}</td>
                      {row.values.map((v, i) => (
                        row.editable && canEdit ? (
                          <td key={provs[i]} className="text-center p-0 border border-amber-300 bg-amber-50">
                            <input
                              data-testid={`cp-input-${row.key}-${i}`}
                              type="number" step="0.00001"
                              value={edits[row.key]?.[provs[i]] ?? 0}
                              onChange={(e) => setEdits((prev) => ({
                                ...prev,
                                [row.key]: { ...(prev[row.key] || {}), [provs[i]]: e.target.value },
                              }))}
                              onBlur={() => saveManual(row.key)}
                              className="w-24 text-center py-2 px-1 bg-transparent tabular-nums focus:outline-none focus:bg-white"
                            />
                          </td>
                        ) : (
                          <td key={provs[i]} className={`text-center py-2 px-3 border border-slate-200 tabular-nums ${row.bold ? "font-bold" : ""}`}>
                            {fmtVal(v, row.fmt)}
                          </td>
                        )
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="text-sm space-y-3 pt-24">
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 text-xs max-w-[240px]">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Las celdas en amarillo (PRIMA, FLETE US, IEPS, IMPORTADORA) son captura manual por proveedor. Se guarda automáticamente al salir de la celda.</span>
                </div>
                <div className="border border-slate-200 rounded-md p-3 space-y-2 max-w-[240px]">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-bold text-slate-700">T.C.</span>
                    <span className="tabular-nums" data-testid="cp-param-tc">{Number(data.params.tc || 0).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 -mt-1">Promedio DOF del mes (automático)</p>
                  <div className="flex items-center justify-between gap-3">
                    <label className="font-bold text-slate-700">Densidad</label>
                    <input data-testid="cp-param-densidad" type="number" step="0.001" disabled={!canEdit}
                      value={paramEdits.densidad}
                      onChange={(e) => setParamEdits((p) => ({ ...p, densidad: e.target.value }))}
                      onBlur={saveParams}
                      className="w-24 text-right rounded border border-slate-300 px-2 py-1 tabular-nums focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:bg-slate-100" />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <label className="font-bold text-slate-700">Litros x galón</label>
                    <input data-testid="cp-param-litros" type="number" step="0.00001" disabled={!canEdit}
                      value={paramEdits.litros_galon}
                      onChange={(e) => setParamEdits((p) => ({ ...p, litros_galon: e.target.value }))}
                      onBlur={saveParams}
                      className="w-24 text-right rounded border border-slate-300 px-2 py-1 tabular-nums focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:bg-slate-100" />
                  </div>
                  <p className="text-[10px] text-slate-400">Se guardan automáticamente al salir del campo.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
